import { useEffect, useState } from "react";
import { X, User, EnvelopeSimple, Buildings, Phone, ToggleLeft, ToggleRight } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/** Edit an existing client's name / email / company / phone / active state. */
export function EditClientModal({ open, client, onClose, onSaved }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [active, setActive] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    setName(client.name || "");
    setEmail(client.email || "");
    setCompany(client.company_name || "");
    setPhone(client.phone || "");
    setActive(client.active !== false);
  }, [open, client]);

  if (!open || !client) return null;

  const dirty = (
    name.trim() !== (client.name || "") ||
    email.trim().toLowerCase() !== (client.email || "").toLowerCase() ||
    company.trim() !== (client.company_name || "") ||
    phone.trim() !== (client.phone || "") ||
    active !== (client.active !== false)
  );

  const validate = () => {
    if (!name.trim()) return "Name is required";
    if (!email.trim() || !email.includes("@")) return "Valid email is required";
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = validate();
    if (v !== true) return toast.error(v);
    if (!dirty) { onClose(); return; }
    setSaving(true);
    try {
      const payload = {};
      if (name.trim() !== (client.name || "")) payload.name = name.trim();
      if (email.trim().toLowerCase() !== (client.email || "").toLowerCase()) payload.email = email.trim().toLowerCase();
      if (company.trim() !== (client.company_name || "")) payload.company_name = company.trim() || null;
      if (phone.trim() !== (client.phone || "")) payload.phone = phone.trim() || null;
      if (active !== (client.active !== false)) payload.active = active;
      const { data } = await api.patch(`/admin/clients/${client.id}`, payload);
      toast.success(`Updated ${data.name}`);
      onSaved?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" data-testid="edit-client-modal">
      <div className="bg-[#0a0a0a] border border-[#FF2D2D] w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-[#262626]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF2D2D]">Edit client</div>
            <div className="font-display text-2xl mt-1">{client.name}</div>
          </div>
          <button onClick={onClose} className="p-1.5 text-[#a3a3a3] hover:text-[#FF2D2D]" aria-label="Close" data-testid="edit-client-close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={submit} className="p-5 space-y-4">
          <Field label="Name" required icon={User}>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)}
              data-testid="edit-client-name" maxLength={200}
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
          </Field>
          <Field label="Email" required icon={EnvelopeSimple}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="edit-client-email" maxLength={200}
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
          </Field>
          <Field label="Company" icon={Buildings}>
            <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
              data-testid="edit-client-company" maxLength={200}
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
          </Field>
          <Field label="Phone" icon={Phone}>
            <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
              data-testid="edit-client-phone" maxLength={40}
              className="w-full bg-[#050505] border border-[#262626] px-3 py-2 text-white focus:border-[#FF2D2D] outline-none" />
          </Field>

          <button type="button" onClick={() => setActive((v) => !v)} data-testid="edit-client-active-toggle"
            className={`w-full flex items-center justify-between p-3 border transition-colors ${active ? "border-[#28C878]/40 bg-[#28C878]/5" : "border-[#FF2D2D]/40 bg-[#FF2D2D]/5"}`}>
            <div className="text-left">
              <div className="text-sm text-white flex items-center gap-2">
                {active ? <ToggleRight size={16} className="text-[#28C878]" weight="fill" /> : <ToggleLeft size={16} className="text-[#FF2D2D]" weight="fill" />}
                Account {active ? "active" : "deactivated"}
              </div>
              <div className="text-[10px] text-[#a3a3a3] uppercase tracking-wider mt-0.5">
                {active ? "Client can log in and see their portal" : "Login blocked. Projects preserved."}
              </div>
            </div>
          </button>

          <div className="flex justify-end gap-3 pt-3 border-t border-[#262626]">
            <button type="button" onClick={onClose} className="btn-outline px-4 py-2 text-xs uppercase tracking-wider">Cancel</button>
            <button type="submit" disabled={saving || !dirty} data-testid="edit-client-save"
              className="btn-primary px-5 py-2 text-xs uppercase tracking-wider disabled:opacity-50">
              {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, required, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-1.5">
        {Icon && <Icon size={11} weight="bold" />}
        {label} {required && <span className="text-[#FF2D2D]">*</span>}
      </label>
      {children}
    </div>
  );
}
